import React from 'react';
export type StatCardVariant = 'blue' | 'teal' | 'purple' | 'pink' | 'green' | 'orange' | 'lime' | 'slate';
interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  subValue?: string | number;
  variant?: StatCardVariant;
  fullWidth?: boolean;
  className?: string;
  children?: React.ReactNode;
}
const variantStyles: Record<StatCardVariant, {
  bg: string;
  header: string;
  body: string;
}> = {
  blue: {
    bg: 'bg-blue-600',
    header: 'bg-[#1e3a5f]',
    body: 'bg-blue-600'
  },
  teal: {
    bg: 'bg-teal-600',
    header: 'bg-[#0f766e]',
    body: 'bg-teal-600'
  },
  purple: {
    bg: 'bg-purple-600',
    header: 'bg-[#581c87]',
    body: 'bg-purple-600'
  },
  pink: {
    bg: 'bg-pink-600',
    header: 'bg-[#be185d]',
    body: 'bg-pink-500'
  },
  green: {
    bg: 'bg-green-600',
    header: 'bg-[#15803d]',
    body: 'bg-green-600'
  },
  orange: {
    bg: 'bg-orange-500',
    header: 'bg-[#c2410c]',
    body: 'bg-orange-500'
  },
  lime: {
    bg: 'bg-lime-500',
    header: 'bg-[#65a30d]',
    body: 'bg-lime-500'
  },
  slate: {
    bg: 'bg-slate-600',
    header: 'bg-slate-800',
    body: 'bg-slate-600'
  }
};
export function StatCard({
  title,
  value,
  subtitle,
  subValue,
  variant = 'blue',
  fullWidth = false,
  className = '',
  children
}: StatCardProps) {
  const styles = variantStyles[variant];
  // If children are provided, we render a custom layout (like the split cards in the image)
  if (children) {
    return <div className={`rounded-lg overflow-hidden shadow-lg text-white ${className} ${fullWidth ? 'w-full' : ''}`} data-id="element-91">
        <div className={`${styles.header} p-3 text-center`} data-id="element-92">
          <h3 className="text-sm font-medium opacity-90" data-id="element-93">{title}</h3>
          <p className="text-2xl font-bold mt-1" data-id="element-94">{value}</p>
        </div>
        <div className={`${styles.body} p-4`} data-id="element-95">{children}</div>
      </div>;
  }
  // Standard card layout
  return <div className={`rounded-lg overflow-hidden shadow-lg text-white ${styles.body} ${className} ${fullWidth ? 'w-full' : ''}`} data-id="element-96">
      <div className="p-4" data-id="element-97">
        <h3 className="text-sm font-medium opacity-90 mb-1" data-id="element-98">{title}</h3>
        <p className="text-2xl font-bold" data-id="element-99">{value}</p>
        {(subtitle || subValue) && <div className="mt-4 pt-3 border-t border-white/20 flex justify-between items-center text-sm" data-id="element-100">
            <span data-id="element-101">{subtitle}</span>
            <span className="font-bold" data-id="element-102">{subValue}</span>
          </div>}
      </div>
    </div>;
}
// Helper component for the split sections inside a StatCard (as seen in the image)
export function StatRow({
  label,
  value
}: {
  label: string;
  value: string | number;
}) {
  return <div className="flex justify-between items-center mb-2 last:mb-0" data-id="element-103">
      <span className="text-sm opacity-90" data-id="element-104">{label}</span>
      <span className="font-bold" data-id="element-105">{value}</span>
    </div>;
}
export function StatGrid({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-106">{children}</div>;
}